import {
  BLUE_LOADING_BUTTON,
  SMALL_INPUT_DARK,
  SMALL_LABEL,
  SMALL_SELECT_DARK,
} from "portal/theme/theme";
import { buildPermission } from "portal/utils/auth";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { EditableTextRow } from "portal/components/EditableTextRow";
import {
  formatImplementationStatus,
  ImplementationStatus,
  toImplementationStatus,
} from "portal/utils/robots";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotSummaryResponse } from "protos/portal/robots";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { values } from "portal/utils/objects";
import React, { FunctionComponent, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

const _RobotImplementationSelector: FunctionComponent<{
  summary?: RobotSummaryResponse;
  readOnly?: boolean;
}> = ({ summary, readOnly = false }) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [implementationStatus, setImplementationStatus] =
    useState<ImplementationStatus>(
      toImplementationStatus(summary?.robot?.implementationStatus)
    );

  const [updateRobot, { isLoading }] = useMutationPopups(
    portalApi.endpoints.updateRobot.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.robots.robot_one"),
        })
      ),
    }
  );

  const canUpdate =
    useAuthorizationRequired({
      or: [
        buildPermission(
          PermissionAction.update,
          PermissionResource.robots,
          PermissionDomain.customer
        ),
        buildPermission(
          PermissionAction.update,
          PermissionResource.robots,
          PermissionDomain.all
        ),
      ],
    }) && !readOnly;

  const text = `${t(
    "components.RobotImplementationSelector.status"
  )}: ${formatImplementationStatus(t, summary?.robot?.implementationStatus)}`;

  return (
    <>
      <EditableTextRow
        text={text}
        onEdit={
          canUpdate
            ? () => {
                setImplementationStatus(
                  toImplementationStatus(summary?.robot?.implementationStatus)
                );
                setOpen(true);
              }
            : undefined
        }
      />
      <Dialog open={isOpen} onClose={() => setOpen(false)}>
        <DialogTitle>
          {t("components.RobotImplementationSelector.title")}
        </DialogTitle>
        <DialogContent>
          <FormControl>
            <InputLabel {...SMALL_LABEL}>
              {t("components.RobotImplementationSelector.status")}
            </InputLabel>
            <Select
              {...SMALL_SELECT_DARK}
              input={
                <OutlinedInput
                  {...SMALL_INPUT_DARK}
                  label={t("components.RobotImplementationSelector.status")}
                />
              }
              defaultValue={ImplementationStatus.ACTIVE}
              value={implementationStatus}
              onChange={(event) =>
                setImplementationStatus(
                  toImplementationStatus(event.target.value)
                )
              }
              renderValue={(status) => formatImplementationStatus(t, status)}
            >
              {values(ImplementationStatus).map((status) => (
                <MenuItem value={status} key={status}>
                  {formatImplementationStatus(
                    t,
                    toImplementationStatus(status)
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            disabled={isLoading}
            onClick={() => setOpen(false)}
          >
            {t("utils.actions.cancel")}
          </Button>
          <Button
            {...BLUE_LOADING_BUTTON}
            loading={isLoading}
            startIcon={<SaveIcon />}
            onClick={async () => {
              if (!summary?.robot) {
                return;
              }
              await updateRobot({
                serial: summary.robot.serial,
                robot: { implementationStatus },
              });
              setOpen(false);
            }}
          >
            {t("utils.actions.saveLong", {
              subject: t("components.RobotImplementationSelector.status"),
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RobotImplementationSelector = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.all
      ),
    ],
  },
  _RobotImplementationSelector
);
